import './FilterOptionSecondLevelCollections.scss';

import Button from '@components/Button';
import { ArrowIcon } from '@components/Icon/Arrow';
import { createClsNameMap, stripHtml } from '@utils';

import type { FilterOptionItemSettings } from '@/features/filter-product-list/filter/FilterOptionItem';
import { FilterOptionItem } from '@/features/filter-product-list/filter/FilterOptionItem';
import FilterOptionItemButton from '@/features/filter-product-list/filter/FilterOptionItem/Button';
import FilterOptionItemLabel from '@/features/filter-product-list/filter/FilterOptionItem/Label';
import type {
  FilterOptionCollectionItemHookProps,
  FilterOptionCollectionItemProps,
} from '@/features/filter-product-list/filter/FilterOptionMultiLevelCollections';

import FilterOptionThirdLevelCollections from '../FilterOptionThirdLevelCollections';

export const clsNameMap = createClsNameMap({
  elements: {
    title: createClsNameMap(),
  },
})('filter-option-item-multilevel-collections-secondlevel');

export type FilterOptionSecondLevelCollectionsItemProps = {
  collectionId: string;
  collectionHandle: string;
  useFilterOptionCollectionItem: (
    item: FilterOptionCollectionItemHookProps
  ) => FilterOptionCollectionItemProps;
  value: {
    tag: string;
    displayName: string;
    subTags?: Array<{
      tag: string;
      displayName: string;
    }>;
    settings: FilterOptionItemSettings;
  };
};

export type FilterOptionSecondLevelCollectionsProps = {
  collectionId: string;
  collectionHandle: string;
  useFilterOptionCollectionItem: (
    item: FilterOptionCollectionItemHookProps
  ) => FilterOptionCollectionItemProps;
  options: Array<FilterOptionSecondLevelCollectionsItemProps['value']>;
};

const FilterOptionSecondLevelCollectionsItem = ({
  collectionHandle,
  value: { displayName, tag, subTags, settings },
  collectionId,
  useFilterOptionCollectionItem,
}: FilterOptionSecondLevelCollectionsItemProps) => {
  const {
    collectionUrlPath,
    isOptionItemSelected,
    isChildListExpanded,
    toggleChildListExpandedState,
    handleSelectOptionItem,
  } = useFilterOptionCollectionItem({
    collectionId,
    collectionHandle,
    tag,
    subTags,
    hasChildListFilterOptionItem: !!subTags?.length,
  });

  if (!stripHtml(displayName)) return null;

  return (
    <>
      <FilterOptionItem as='div' className={clsNameMap.elm('title')}>
        <FilterOptionItemButton
          as='link'
          isSelected={isOptionItemSelected}
          label={<FilterOptionItemLabel settings={settings} label={displayName} />}
          link={collectionUrlPath}
          onClick={(e: Event) => handleSelectOptionItem(e)}
        />
        {!!subTags?.length && (
          <Button
            aria-label={`Expand/Collapse ${displayName}`}
            modifiers={['transparent']}
            style={{ paddingRight: 0 }}
            onClick={toggleChildListExpandedState}
          >
            <ArrowIcon modifiers={isChildListExpanded ? ['down'] : ['right']} />
          </Button>
        )}
      </FilterOptionItem>
      {!!subTags?.length && isChildListExpanded && (
        <FilterOptionThirdLevelCollections
          useFilterOptionCollectionItem={useFilterOptionCollectionItem}
          collectionId={collectionId}
          collectionHandle={collectionHandle}
          options={subTags.map((item) => ({
            ...item,
            settings,
          }))}
        />
      )}
    </>
  );
};

const FilterOptionSecondLevelCollections = ({
  options,
  collectionId,
  collectionHandle,
  useFilterOptionCollectionItem,
}: FilterOptionSecondLevelCollectionsProps) => {
  return (
    <>
      {options.map((option, i) => (
        <li key={i} className={clsNameMap.root()}>
          <FilterOptionSecondLevelCollectionsItem
            key={option.tag}
            collectionId={collectionId}
            collectionHandle={collectionHandle}
            value={option}
            useFilterOptionCollectionItem={useFilterOptionCollectionItem}
          />
        </li>
      ))}
    </>
  );
};

export default FilterOptionSecondLevelCollections;
